import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Avatar,
  Button,
  Input,
  Progress,
  Spin,
  Typography,
  message,
} from 'antd';
import {
  ApiOutlined,
  ArrowUpOutlined,
  CodeOutlined,
  DatabaseOutlined,
  DeploymentUnitOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  RobotOutlined,
  StopOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import html2pdf from 'html2pdf.js';
import './AgentRuntime.css';

// PDF 文件名后缀（平台品牌）。集中一处，便于统一调整。
const PDF_BRAND_SUFFIX = '智坊智能体平台';
import VoiceInputButton from '../../components/VoiceInputButton';
import {
  AgentConversation,
  AgentMessage,
  LoadedAgent,
  getAgentTask,
  listConversations,
  listLoadedAgents,
  listMessages,
  sendAgentMessage,
} from '../../services/agentRuntimeApi';

const { Text } = Typography;
const { TextArea } = Input;

function createUserMessage(content: string): AgentMessage {
  return {
    id: `user-${Date.now()}`,
    role: 'user',
    type: 'text',
    content,
    createdAt: new Date().toISOString(),
    status: 'sent',
  };
}

function formatTime(value: string) {
  return new Date(value).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
}


function formatDateTime(value: string) {
  return new Date(value).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function stripHtml(html: string) {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

function getMessagePreview(msg: AgentMessage) {
  if (msg.type === 'html') {
    const html = typeof msg.content === 'string' ? msg.content : String(msg.content?.content || '');
    return stripHtml(html).slice(0, 72) || 'HTML 报告已生成，点击查看完整内容';
  }
  if (msg.type === 'task') {
    const task = typeof msg.content === 'object' ? msg.content : { title: String(msg.content) };
    return task.current || task.message || task.title || '任务状态更新';
  }
  const text = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content || {}, null, 2);
  return text.replace(/\s+/g, ' ').trim().slice(0, 72) || '智能体返回结果';
}

function getMessageTitle(msg: AgentMessage, agentName?: string) {
  if (msg.type === 'html') return 'HTML 结果报告';
  if (msg.type === 'task') {
    const task = typeof msg.content === 'object' ? msg.content : { title: String(msg.content) };
    return task.title || '任务执行状态';
  }
  if (msg.type === 'json' || msg.type === 'table') return '结构化结果';
  return agentName ? `${agentName} 的回复` : '智能体结果';
}

// 取"内容自身的标题"作为文件名主体：HTML 报告取 <title>/<h1>，
// 结构化结果取 content.title，文本/markdown 取首行非空文字，兜底用消息标题。
function getContentTitle(msg: AgentMessage, agentName?: string): string {
  if (msg.type === 'html') {
    const raw = typeof msg.content === 'string' ? msg.content : String((msg.content as any)?.content || '');
    const m = raw.match(/<title[^>]*>([\s\S]*?)<\/title>/i)
      || raw.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i)
      || raw.match(/<h2[^>]*>([\s\S]*?)<\/h2>/i);
    const t = m ? stripHtml(m[1]).trim() : '';
    if (t) return t;
  }
  if (msg.content && typeof msg.content === 'object' && (msg.content as any).title) {
    return String((msg.content as any).title);
  }
  if (typeof msg.content === 'string' && msg.content.trim()) {
    const firstLine = msg.content.split('\n').map(s => s.trim()).find(Boolean);
    if (firstLine) return firstLine.replace(/^#+\s*/, '').replace(/^[#*>\-\s]+/, '').slice(0, 40);
  }
  return getMessageTitle(msg, agentName);
}

function buildPdfFilename(msg: AgentMessage, agentName?: string): string {
  const title = getContentTitle(msg, agentName) || '智能体结果';
  return `${title}_${PDF_BRAND_SUFFIX}`
    .replace(/[\\/:*?"<>|\r\n\t]+/g, '_')
    .replace(/\s+/g, '')
    .slice(0, 90) + '.pdf';
}

// 详情内容导出 PDF：客户端直接生成并触发浏览器下载（无打印弹窗）。
// HTML 报告用离屏 iframe 渲染原始 HTML（样式隔离），再交给 html2pdf 截取分页；
// 文本/markdown/JSON 复用屏幕上已渲染的详情 DOM，做到所见即所得。
async function exportDetailToPdf(msg: AgentMessage, agentName?: string) {
  const filename = buildPdfFilename(msg, agentName);
  const isHtml = msg.type === 'html';
  const rawHtml = typeof msg.content === 'string' ? msg.content : String((msg.content as any)?.content || '');
  const opt = {
    margin: [10, 10, 12, 10],
    filename,
    image: { type: 'jpeg', quality: 0.96 },
    html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: 794 },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    pagebreak: { mode: ['css', 'legacy'] },
  };
  const hide = message.loading('正在生成 PDF...', 0);
  let iframe: HTMLIFrameElement | null = null;
  let host: HTMLDivElement | null = null;
  try {
    let target: HTMLElement;
    if (isHtml && rawHtml.trim()) {
      // 离屏 iframe 渲染原始 HTML，隔离其 <style>，避免污染主页面
      iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;left:-99999px;top:0;width:794px;height:1123px;border:0;';
      document.body.appendChild(iframe);
      const idoc = iframe.contentDocument!;
      idoc.open();
      idoc.write(rawHtml);
      idoc.close();
      await new Promise(r => setTimeout(r, 450)); // 等样式/图片
      iframe.style.height = `${Math.max(1123, idoc.body.scrollHeight)}px`;
      await new Promise(r => setTimeout(r, 100));
      target = idoc.body;
    } else {
      const bodyEl = document.querySelector('.agent-runtime-detail-body');
      host = document.createElement('div');
      host.style.cssText = 'position:fixed;left:-99999px;top:0;width:794px;padding:24px;background:#fff;color:#0f172a;font-family:-apple-system,"PingFang SC","Microsoft YaHei",sans-serif;line-height:1.78;font-size:13px;white-space:pre-wrap;word-break:break-word;';
      host.innerHTML = bodyEl
        ? bodyEl.innerHTML
        : `<div>${typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2)}</div>`;
      document.body.appendChild(host);
      await new Promise(r => setTimeout(r, 150));
      target = host;
    }
    await html2pdf().set(opt).from(target).save();
    message.success('PDF 已开始下载');
  } catch (e: any) {
    message.error(`PDF 生成失败：${e?.message || e}`);
  } finally {
    hide();
    if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
    if (host && host.parentNode) host.parentNode.removeChild(host);
  }
}

function isCompactResultMessage(msg: AgentMessage) {
  // 历史里加载的 error 消息没有 status 字段，不能伪装成"结果卡片"让用户点开看到一坨错误 JSON
  return msg.role === 'agent' && msg.type !== 'task' && msg.type !== 'error' && msg.status !== 'failed';
}

function createHistoryDivider(record: AgentConversation, index: number): AgentMessage {
  return {
    id: `history-divider-${record.id}`,
    role: 'system',
    type: 'text',
    content: index === 0 ? '最近一条历史记录' : `更早历史记录 · ${record.title || '历史任务'}`,
    createdAt: record.updatedAt || new Date().toISOString(),
    status: 'sent',
  };
}

function getAgentInitial(agent?: LoadedAgent | null) {
  const name = agent?.name || '智件';
  return name.replace(/智能体|助手|Agent/gi, '').trim().slice(0, 2) || name.slice(0, 2);
}

function getAgentTone(agent: LoadedAgent, index: number) {
  const text = `${agent.category || ''}${agent.subCategory || ''}${agent.name || ''}`;
  if (/文档|办公|报告|材料/.test(text)) return 'doc';
  if (/运维|监控|告警|系统/.test(text)) return 'ops';
  if (/数据|知识|分析|检索/.test(text)) return 'data';
  return ['blue', 'mint', 'sky', 'slate'][index % 4];
}

function getAgentVisual(agent: LoadedAgent, index: number) {
  const tone = getAgentTone(agent, index);
  const text = `${agent.category || ''}${agent.subCategory || ''}${agent.name || ''}${agent.agentCode || ''}`;
  let icon = <RobotOutlined />;
  if (/运维|监控|告警|ops|operation/i.test(text)) icon = <DeploymentUnitOutlined />;
  else if (/数据|知识|分析|检索/.test(text)) icon = <DatabaseOutlined />;
  else if (/文档|办公|报告|材料/.test(text)) icon = <FileTextOutlined />;
  else if (/接口|api|服务|网关/i.test(text)) icon = <ApiOutlined />;
  else if (/代码|开发|工程/.test(text)) icon = <CodeOutlined />;
  return { tone, icon };
}

function extractHtmlFromTaskResult(result: any): string | null {
  if (!result || typeof result !== 'object') return null;
  // 严格遵守智能体协议：只有 replyType === 'html' 且 content 为非空字符串时才按 HTML 报告渲染。
  // 如果智能体只返回 format/fileId/html/htmlContent 等非协议字段，前端不猜测，直接展示原始 JSON 方便定位智能体侧问题。
  if (result.replyType === 'html' && typeof result.content === 'string') {
    const html = result.content.trim();
    return html ? result.content : null;
  }
  return null;
}

function normalizeTaskResultMessage(result: any, fallbackTitle = '智能体任务结果'): AgentMessage {
  const html = extractHtmlFromTaskResult(result);
  if (html) {
    return {
      id: `agent-result-${Date.now()}`,
      role: 'agent',
      type: 'html',
      content: html,
      createdAt: new Date().toISOString(),
      status: 'sent',
    };
  }
  const type = result?.replyType === 'markdown' ? 'markdown' : result?.replyType === 'text' ? 'text' : 'json';
  return {
    id: `agent-result-${Date.now()}`,
    role: 'agent',
    type,
    content: type === 'json' ? { title: fallbackTitle, ...result } : (result?.content || result?.message || JSON.stringify(result, null, 2)),
    createdAt: new Date().toISOString(),
    status: 'sent',
  };
}

const FINAL_TASK_STATUSES = new Set(['success', 'completed', 'failed', 'cancelled', 'timeout', 'force-stopped', 'service-unavailable']);
const TASK_TIMEOUT_MS = 10 * 60 * 1000;

function isTaskRunning(content: any) {
  if (!content || typeof content !== 'object' || !content.taskId) return false;
  return !FINAL_TASK_STATUSES.has(String(content.status || 'running'));
}


function normalizeStaleTaskMessage(msg: AgentMessage): AgentMessage {
  if (msg.type !== 'task' || typeof msg.content !== 'object' || !msg.content?.taskId) return msg;
  const status = String(msg.content.status || 'running');
  if (FINAL_TASK_STATUSES.has(status)) return msg;
  const createdAt = new Date(msg.createdAt).getTime();
  if (!Number.isFinite(createdAt) || Date.now() - createdAt < TASK_TIMEOUT_MS) return msg;
  return {
    ...msg,
    content: {
      ...msg.content,
      status: 'running',
      progress: Number.isFinite(Number(msg.content.progress)) ? msg.content.progress : 0,
      polling: true,
      current: '历史任务状态待同步，正在重新查询智能体任务接口...',
      updatedAt: new Date().toISOString(),
    },
  };
}

function renderContent(msg: AgentMessage, onForceStop?: (taskMessageId: string, taskId: string) => void) {
  if (msg.type === 'html') {
    const html = typeof msg.content === 'string' ? msg.content : String(msg.content?.content || '');
    return (
      <div className="agent-runtime-html-frame">
        <iframe
          title="智能体HTML报告"
          sandbox="allow-same-origin"
          srcDoc={html}
        />
      </div>
    );
  }
  if (msg.type === 'json' || msg.type === 'table') {
    return <pre className="agent-runtime-code">{JSON.stringify(msg.content, null, 2)}</pre>;
  }
  if (msg.type === 'task') {
    const task = typeof msg.content === 'object' ? msg.content : { title: String(msg.content) };
    const percent = Number.isFinite(Number(task.progress)) ? Math.max(0, Math.min(100, Number(task.progress))) : 0;
    const status = String(task.status || 'running');
    const isStopped = status === 'cancelled' || status === 'force-stopped';
    const isRetryingError = status === 'polling-error';
    const isFailed = status === 'failed' || status === 'service-unavailable' || status === 'timeout';
    const isDone = status === 'success' || status === 'completed' || isFailed || isStopped;
    const canForceStop = !isDone && Boolean(task.taskId);
    const statusLabel = status === 'success' || status === 'completed' ? '已完成'
      : status === 'failed' ? '已失败'
      : status === 'cancelled' ? '已强制停止'
      : status === 'force-stopped' ? '已强制停止'
      : status === 'polling-error' ? '服务异常，自动重试中'
      : status === 'service-unavailable' ? '服务不可用'
      : status === 'timeout' ? '已超时失败'
      : '执行中';
    const current = (isFailed || isRetryingError)
      ? task.current || task.message || `查询失败: ${task.current || task.message || '未知错误'} 已自动重试 ${task.refreshCount || 0} 次`
      : task.current || task.message || (isDone ? '任务已完成' : `后台执行中，已刷新 ${task.refreshCount || 0} 次`);
    const details = [
      task.taskId ? `任务ID：${task.taskId}` : '',
      `状态：${statusLabel}`,
      task.updatedAt ? `更新时间：${new Date(task.updatedAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : '',
    ].filter(Boolean);
    return (
      <div className="agent-runtime-task">
        <div className="agent-runtime-task-head">
          <ThunderboltOutlined />
          <span>{task.title || (isDone ? '任务已结束' : '任务执行中')}</span>
          <b>{isFailed ? '失败' : isRetryingError ? '重试' : isStopped ? '停止' : `${percent}%`}</b>
          {canForceStop && onForceStop && (
            <Button
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => onForceStop(msg.id, String(task.taskId))}
            >
              强行停止
            </Button>
          )}
        </div>
        <Progress
          // 轮询临时失败(polling-error)时保持真实进度：强制100%红条会让用户误以为"任务100%了但报错"
          percent={isFailed ? 100 : percent}
          showInfo={false}
          status={(isFailed || isRetryingError) ? 'exception' : isDone ? 'success' : 'active'}
          strokeColor={(isFailed || isRetryingError) ? '#dc2626' : '#0891b2'}
          trailColor="#dbeafe"
        />
        <Text type={(isFailed || isRetryingError) ? 'danger' : undefined}>{current}</Text>
        {details.length > 0 && <div className="agent-runtime-task-meta">{details.map(item => <span key={item}>{item}</span>)}</div>}
      </div>
    );
  }
  const text = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2);
  return <div className="agent-runtime-markdown">{text}</div>;
}

export default function AgentRuntimePage() {
  const [agents, setAgents] = useState<LoadedAgent[]>([]);
  const [selectedAgentCode, setSelectedAgentCode] = useState<string>();
  const [conversations, setConversations] = useState<AgentConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string>();
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [visibleHistoryCount, setVisibleHistoryCount] = useState(1);
  const [loadingOlderHistory, setLoadingOlderHistory] = useState(false);
  const [input, setInput] = useState('');
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [detailMessage, setDetailMessage] = useState<AgentMessage | null>(null);
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const taskPollTimersRef = useRef<number[]>([]);
  const polledTaskIdsRef = useRef<Set<string>>(new Set());
  const stoppedTaskIdsRef = useRef<Set<string>>(new Set());
  const skipNextAutoScrollRef = useRef(false);

  useEffect(() => {
    return () => {
      taskPollTimersRef.current.forEach(timer => window.clearTimeout(timer));
      taskPollTimersRef.current = [];
    };
  }, []);

  const selectedAgent = useMemo(
    () => agents.find(agent => agent.agentCode === selectedAgentCode) || null,
    [agents, selectedAgentCode],
  );

  useEffect(() => {
    let mounted = true;
    setLoadingAgents(true);
    listLoadedAgents()
      .then(rows => {
        if (!mounted) return;
        const preferred = rows.find(agent => /运维|监控|告警|ops|operation/i.test(`${agent.name} ${agent.agentCode} ${agent.category || ''} ${agent.subCategory || ''}`));
        setAgents(rows);
        setSelectedAgentCode((preferred || rows[0])?.agentCode);
      })
      .catch(err => message.error(`加载智能体失败：${err?.message || '未知错误'}`))
      .finally(() => mounted && setLoadingAgents(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!selectedAgentCode) return;
    let mounted = true;
    setLoadingMessages(true);
    setVisibleHistoryCount(1);
    listConversations(selectedAgentCode)
      .then(async rows => {
        if (!mounted) return;
        setConversations(rows);
        const latest = rows[0];
        const nextId = latest?.id || `local-${selectedAgentCode}`;
        setActiveConversationId(nextId);
        const loaded = latest ? await listMessages(selectedAgentCode, latest.id) : [];
        if (mounted) {
          setMessages(latest ? [createHistoryDivider(latest, 0), ...loaded.map(normalizeStaleTaskMessage)] : []);
        }
      })
      .finally(() => mounted && setLoadingMessages(false));
    return () => { mounted = false; };
  }, [selectedAgentCode]);

  useEffect(() => {
    if (loadingMessages || !selectedAgentCode) return;
    messages
      .filter(m => m.type === 'task' && typeof m.content === 'object' && m.content?.taskId && isTaskRunning(m.content))
      .forEach((m: any) => {
        const tid = String(m.content.taskId);
        if (polledTaskIdsRef.current.has(tid)) return;
        polledTaskIdsRef.current.add(tid);
        startTaskPolling(selectedAgentCode, m.id, tid);
      });
  }, [messages, loadingMessages, selectedAgentCode]);

  useEffect(() => {
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false;
      return;
    }
    const list = listRef.current;
    if (!list) return;
    const scrollToBottom = () => {
      list.scrollTop = list.scrollHeight;
    };
    scrollToBottom();
    window.requestAnimationFrame(scrollToBottom);
    const timer = window.setTimeout(scrollToBottom, 80);
    return () => window.clearTimeout(timer);
  }, [messages, sending]);

  const selectAgent = (agentCode: string, closeSelector = true) => {
    setSelectedAgentCode(agentCode);
    setInput('');
    setSelectorOpen(!closeSelector);
    polledTaskIdsRef.current.clear();
    stoppedTaskIdsRef.current.clear();
    taskPollTimersRef.current.forEach(timer => window.clearTimeout(timer));
    taskPollTimersRef.current = [];
  };

  const loadOlderHistory = async () => {
    if (!selectedAgentCode || loadingOlderHistory || visibleHistoryCount >= conversations.length) return;
    const nextIndex = visibleHistoryCount;
    const record = conversations[nextIndex];
    if (!record) return;
    const list = listRef.current;
    const previousHeight = list?.scrollHeight || 0;
    setLoadingOlderHistory(true);
    try {
      const loaded = await listMessages(selectedAgentCode, record.id);
      const normalized = [createHistoryDivider(record, nextIndex), ...loaded.map(normalizeStaleTaskMessage)];
      skipNextAutoScrollRef.current = true;
      setMessages(prev => [...normalized, ...prev]);
      setVisibleHistoryCount(nextIndex + 1);
      window.requestAnimationFrame(() => {
        if (list) list.scrollTop = Math.max(0, list.scrollHeight - previousHeight + 12);
      });
    } finally {
      setLoadingOlderHistory(false);
    }
  };

  const handleMessagesScroll = () => {
    if (!listRef.current || loadingMessages || loadingOlderHistory) return;
    if (listRef.current.scrollTop <= 28) loadOlderHistory();
  };

  const startTaskPolling = (agentCode: string, taskMessageId: string, taskId: string) => {
    stoppedTaskIdsRef.current.delete(taskId);
    let finalized = false;
    let attempts = 0;
    let consecutiveErrors = 0;
    const schedule = (delay = 2500) => {
      const timer = window.setTimeout(tick, delay);
      taskPollTimersRef.current.push(timer);
    };
    const tick = async () => {
      if (stoppedTaskIdsRef.current.has(taskId)) {
        finalized = true;
        return;
      }
      attempts += 1;
      try {
        const task = await getAgentTask(agentCode, taskId);
        consecutiveErrors = 0;
        const status = String(task.status || 'running');
        const done = FINAL_TASK_STATUSES.has(status);
        const progress = Number.isFinite(Number(task.progress)) ? Number(task.progress) : (done ? 100 : Math.min(95, 5 + attempts * 3));
        const title = task.result?.title || task.title || '智能体任务执行中';
        const current = task.message || task.current || (done ? '智能体任务已完成' : `后台仍在执行，已自动刷新 ${attempts} 次`);
        setMessages(prev => prev.map(item => item.id === taskMessageId ? {
          ...item,
          content: {
            ...(typeof item.content === 'object' ? item.content : {}),
            taskId,
            status,
            progress,
            title,
            current,
            polling: !done,
            refreshCount: attempts,
            updatedAt: new Date().toISOString(),
            messageCount: task.result?.messageCount ?? task.messageCount,
          },
        } : item));
        if (done) {
          finalized = true;
          if ((status === 'success' || status === 'completed') && task.result) {
            const resultMessageId = `agent-result-${taskId}`;
            const resultMessage = { ...normalizeTaskResultMessage(task.result, title), id: resultMessageId };
            setMessages(prev => prev.some(item => item.id === resultMessageId)
              ? prev
              : prev.some(item => item.sourceTaskId === taskId) ? prev : [...prev, resultMessage]);
          }
          if (status === 'failed') {
            message.error(task.message || '智能体任务执行失败');
          }
          return;
        }
      } catch (err: any) {
        consecutiveErrors += 1;
        const escalatedStatus = consecutiveErrors >= 5 ? 'service-unavailable' : 'polling-error';
        const shouldStopForUnavailable = consecutiveErrors >= 5;
        setMessages(prev => prev.map(item => {
          if (item.id !== taskMessageId) return item;
          const content = (typeof item.content === 'object' && item.content) ? item.content : {};
          // 任务已经是最终状态（可能由另一条轮询链路先拿到成功结果）时，
          // 绝不能再用轮询错误覆盖成"服务异常"，否则出现"进度100%但显示错误"
          if (FINAL_TASK_STATUSES.has(String(content.status))) return item;
          return {
            ...item,
            content: {
              ...content,
              taskId,
              status: escalatedStatus,
              // 保留最近一次真实进度，不要清零
              progress: Number.isFinite(Number(content.progress)) ? Number(content.progress) : 0,
              current: consecutiveErrors >= 5
                ? `查询后台任务失败：${err?.message || '未知错误'}（连续失败 ${consecutiveErrors} 次，服务可能已不可用）`
                : `查询后台任务状态失败：${err?.message || '未知错误'}，稍后继续重试`,
              polling: !shouldStopForUnavailable,
              refreshCount: attempts,
              updatedAt: new Date().toISOString(),
            },
          };
        }));
        if (shouldStopForUnavailable) {
          finalized = true;
          message.error('智能体服务连续不可用，已标记为失败，可重新发起请求');
          return;
        }
      }
      if (!finalized && attempts < 120) schedule(Math.min(8000, 2000 + attempts * 200));
      else if (!finalized && attempts >= 120) {
        finalized = true;
        setMessages(prev => prev.map(item => item.id === taskMessageId ? {
          ...item,
          content: {
            ...(typeof item.content === 'object' ? item.content : {}),
            taskId,
            status: 'timeout',
            progress: 0,
            current: '任务轮询超时，请刷新页面后重试或重新发起',
            polling: false,
            refreshCount: attempts,
            updatedAt: new Date().toISOString(),
          },
        } : item));
      }
    };
    schedule(800);
  };

  const hasRunningTask = messages.some(item => item.type === 'task' && isTaskRunning(item.content));
  const requestLocked = sending || hasRunningTask;

  const handleForceStop = (taskMessageId: string, taskId: string) => {
    stoppedTaskIdsRef.current.add(taskId);
    polledTaskIdsRef.current.delete(taskId);
    taskPollTimersRef.current.forEach(timer => window.clearTimeout(timer));
    taskPollTimersRef.current = [];
    setSending(false);
    setMessages(prev => prev.map(item => item.id === taskMessageId ? {
      ...item,
      content: {
        ...(typeof item.content === 'object' ? item.content : {}),
        taskId,
        status: 'cancelled',
        progress: 0,
        polling: false,
        current: '已手动强行停止本次任务状态等待，可继续发起新的请求。',
        updatedAt: new Date().toISOString(),
      },
    } : item));
    message.warning('已强行停止当前任务等待，可以继续发送新请求');
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content) return;
    if (!selectedAgent) {
      message.warning('请先选择一个智能体');
      return;
    }
    if (requestLocked) {
      message.warning('当前还有任务在请求中，请等待完成或点击任务卡片里的“强行停止”后再发送');
      return;
    }
    const userMessage = createUserMessage(content);
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setSending(true);
    try {
      const history = [...messages, userMessage]
        .filter(item => item.role !== 'system')
        .slice(-12)
        .map(item => ({ role: item.role === 'agent' ? 'assistant' as const : 'user' as const, content: String(item.content) }));
      const result = await sendAgentMessage({
        agentCode: selectedAgent.agentCode,
        conversationId: activeConversationId?.startsWith('local-') ? undefined : activeConversationId,
        messages: history,
        content,
      });
      if (result.conversationId && result.conversationId !== activeConversationId) {
        setActiveConversationId(result.conversationId);
        setVisibleHistoryCount(1);
      }
      setMessages(prev => [...prev, result.response]);
      const maybeTaskId = result.response?.type === 'task' && typeof result.response.content === 'object'
        ? result.response.content?.taskId
        : undefined;
      if (maybeTaskId) {
        // 先登记到已轮询集合，防止 messages effect 对同一任务再启动第二条轮询链路
        // （双链路会在任务完成瞬间并发查询/落库，历史上出现过完成时轮询 500 报错）
        polledTaskIdsRef.current.add(String(maybeTaskId));
        startTaskPolling(selectedAgent.agentCode, result.response.id, String(maybeTaskId));
      }
    } catch (err: any) {
      const errorMessage = err?.message || '智能体调用失败';
      message.error(errorMessage);
      setMessages(prev => [...prev, {
        id: `agent-error-${Date.now()}`,
        role: 'agent',
        type: 'error',
        content: `智能体调用失败\n\n${errorMessage}`,
        createdAt: new Date().toISOString(),
        status: 'failed',
      }]);
    } finally {
      setSending(false);
    }
  };

  if (!loadingAgents && agents.length === 0) {
    return (
      <div className="agent-runtime-page agent-runtime-page-empty">
        <section className="agent-runtime-empty-card" aria-label="当前未加载任何智能体">
          <span className="agent-runtime-empty-icon"><RobotOutlined /></span>
          <h2>当前未加载任何智能体</h2>
          <p>请先加载业务智能体，然后回到这里发起一次性任务。</p>
        </section>
      </div>
    );
  }

  const visibleAgentCards = agents.slice(0, 3);
  const emptyAgentSlots = Math.max(0, 3 - visibleAgentCards.length);

  return (
    <div className="agent-runtime-page">
      <div className="agent-runtime-shell">
        <section className="agent-runtime-agent-panel">
          <div className="agent-runtime-agent-picker">
            <div className="agent-runtime-card-stack" aria-label="页面常驻智能体卡片">
              {visibleAgentCards.map((agent, index) => {
                const visual = getAgentVisual(agent, index);
                const active = agent.agentCode === selectedAgentCode;
                return (
                  <button
                    key={agent.agentCode}
                    type="button"
                    className={`agent-runtime-mini-agent-card is-${visual.tone} ${active ? 'is-selected' : ''}`}
                    onClick={() => {
                      selectAgent(agent.agentCode, false);
                      setSelectorOpen(true);
                    }}
                    aria-pressed={active}
                  >
                    <span className="agent-runtime-mini-mark">{visual.icon}</span>
                    <span className="agent-runtime-mini-copy">
                      <em>{active ? '当前使用' : '可选智能体'}</em>
                      <strong>{agent.name}</strong>
                    </span>
                    <i>{active ? '已选中' : '选择'}</i>
                  </button>
                );
              })}
              {Array.from({ length: emptyAgentSlots }).map((_, index) => (
                <button key={`empty-${index}`} type="button" className="agent-runtime-mini-agent-card is-empty" disabled>
                  <span className="agent-runtime-mini-mark"><RobotOutlined /></span>
                  <span className="agent-runtime-mini-copy">
                    <em>待加载</em>
                    <strong>智能体空位</strong>
                  </span>
                  <i>空位</i>
                </button>
              ))}
            </div>

            {selectorOpen && (
              <section className="agent-runtime-selector-popout" role="dialog" aria-modal="false">
                <header className="agent-runtime-selector-head">
                  <div>
                    <span className="agent-runtime-eyebrow">LOADED AGENTS</span>
                    <strong>已加载智能体</strong>
                    <p>局部从卡片旁伸出，点击任一智能体立即切换。</p>
                  </div>
                  <button type="button" onClick={() => setSelectorOpen(false)}>收起</button>
                </header>
                <div className="agent-runtime-selector-grid">
                  {agents.map((agent, index) => {
                    const visual = getAgentVisual(agent, index);
                    const active = agent.agentCode === selectedAgentCode;
                    return (
                      <button
                        key={agent.agentCode}
                        type="button"
                        className={`agent-runtime-selector-card is-${visual.tone} ${active ? 'is-active' : ''}`}
                        onClick={() => selectAgent(agent.agentCode)}
                      >
                        <span className="agent-runtime-card-glow" />
                        <span className="agent-runtime-agent-mark">{visual.icon}</span>
                        <span className="agent-runtime-selector-copy">
                          <b>{agent.name}</b>
                          <em>{agent.subCategory || agent.category || '已加载智件'}</em>
                        </span>
                        <i>{active ? '当前使用中' : agent.status === 'online' ? '点击切换' : '可切换'}</i>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
          </div>

          <footer className="agent-runtime-input">
            <TextArea
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={requestLocked ? '当前任务未完成；请等待完成，或点击任务卡片“强行停止”后继续' : (selectedAgent ? `输入要交给「${selectedAgent.name}」处理的任务...` : '先选择一个智能体')}
              disabled={requestLocked}
              autoSize={false}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  if (!requestLocked) handleSend();
                }
              }}
            />
            <div className="agent-runtime-input-bar">
              <span>{requestLocked ? '任务执行中，暂不能重复发送' : 'Enter 发送，Shift+Enter 换行'}</span>
              <VoiceInputButton value={input} onChange={setInput} disabled={requestLocked} size="small" />
              <Button type="primary" icon={<ArrowUpOutlined />} loading={sending} disabled={requestLocked} onClick={handleSend}>
                发送
              </Button>
            </div>
          </footer>
        </section>

        <main className="agent-runtime-dialog-panel">
          <header className="agent-runtime-dialog-head">
            <div>
              <span className="agent-runtime-eyebrow">对话框</span>
              <strong>{selectedAgent?.name || '选择智能体后开始'}</strong>
            </div>
            <em>{requestLocked ? '处理中' : conversations.length > 0 ? `历史 ${visibleHistoryCount}/${conversations.length}` : '就绪'}</em>
          </header>

          <section className="agent-runtime-messages" ref={listRef} onScroll={handleMessagesScroll}>
            {(loadingAgents || loadingMessages) ? <Spin className="agent-runtime-spin" /> : messages.length === 0 ? (
              <div className="agent-runtime-welcome">
                <span>{getAgentInitial(selectedAgent)}</span>
                <h3>直接输入任务</h3>
                <p>历史记录显示在这里；默认只载入最近一条，向上滚动继续加载更早记录。</p>
              </div>
            ) : <>
              {loadingOlderHistory && <div className="agent-runtime-history-loading">正在加载上一条历史记录...</div>}
              {visibleHistoryCount < conversations.length && !loadingOlderHistory && (
                <button type="button" className="agent-runtime-load-older" onClick={loadOlderHistory}>上滑或点击加载上一条历史记录</button>
              )}
              {messages.map(msg => msg.role === 'system' ? (
                <div key={msg.id} className="agent-runtime-history-divider">
                  <span>{String(msg.content)}</span>
                  <em>{formatDateTime(msg.createdAt)}</em>
                </div>
              ) : isCompactResultMessage(msg) ? (
                <button
                  key={msg.id}
                  type="button"
                  className={`agent-runtime-result-card is-${msg.type}`}
                  onClick={() => setDetailMessage(msg)}
                >
                  <span className="agent-runtime-result-icon"><FileTextOutlined /></span>
                  <span className="agent-runtime-result-copy">
                    <em>{formatDateTime(msg.createdAt)} · {msg.type === 'html' ? 'HTML详情' : '结果摘要'}</em>
                    <strong>{getMessageTitle(msg, selectedAgent?.name)}</strong>
                    <small>{getMessagePreview(msg)}</small>
                  </span>
                  <i>打开详情</i>
                </button>
              ) : (
                <div key={msg.id} className={`agent-runtime-message agent-runtime-message-${msg.role}`}>
                  <Avatar icon={msg.role === 'user' ? <UserOutlined /> : <RobotOutlined />} />
                  <div className="agent-runtime-bubble">
                    <div className="agent-runtime-message-meta">
                      <b>{msg.role === 'user' ? '你' : selectedAgent?.name || '智能体'}</b>
                      <span className="agent-runtime-message-time">{formatDateTime(msg.createdAt)}</span>
                    </div>
                    {renderContent(msg, handleForceStop)}
                  </div>
                </div>
              ))}
            </>}
            {sending && (
              <div className="agent-runtime-thinking">
                <span className="agent-runtime-thinking-orbit"><RobotOutlined /></span>
                <span className="agent-runtime-thinking-copy">
                  <b>正在提交给智能体</b>
                  <em>收到任务 ID 后会自动显示真实进度</em>
                </span>
                <span className="agent-runtime-thinking-dots"><i /> <i /> <i /></span>
              </div>
            )}
          </section>
        </main>
      </div>

      {detailMessage && createPortal(
        <div className="agent-runtime-detail-layer" role="dialog" aria-modal="true">
          <button className="agent-runtime-detail-backdrop" type="button" onClick={() => setDetailMessage(null)} aria-label="关闭结果详情" />
          <section className="agent-runtime-detail-modal">
            <header className="agent-runtime-detail-head">
              <div>
                <span className="agent-runtime-eyebrow">RESULT DETAIL</span>
                <strong>{getMessageTitle(detailMessage, selectedAgent?.name)}</strong>
                <p>{formatDateTime(detailMessage.createdAt)} · 点击遮罩或按钮关闭</p>
              </div>
              <div className="agent-runtime-detail-actions">
                <button
                  type="button"
                  className="agent-runtime-detail-pdf"
                  onClick={() => exportDetailToPdf(detailMessage, selectedAgent?.name)}
                >
                  <FilePdfOutlined /> 导出 PDF
                </button>
                <button type="button" onClick={() => setDetailMessage(null)}>关闭</button>
              </div>
            </header>
            <div className="agent-runtime-detail-body">
              {renderContent(detailMessage, handleForceStop)}
            </div>
          </section>
        </div>,
        document.body,
      )}
    </div>
  );
}
